import { type MouseEvent, type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const SCREEN_MARGIN = 8;
const TRIGGER_GAP = 8;
const ARROW_MARGIN = 12;

type TipPosition = {
  top: number;
  left: number;
  arrowLeft: number;
  isBelow: boolean;
};

type HintTriggerProps = {
  label: string;
  hint: string;
  children: ReactNode;
  className?: string;
  /** For an action that cannot run, so assistive technology announces it as unavailable */
  isDisabled?: boolean;
};

// A button whose only job is to explain something: on hover with a mouse, on tap on a touch
// screen. The explanation renders on the page itself, so a card, table or modal cannot clip it.
const HintTrigger = ({ label, hint, children, className = "", isDisabled = false }: HintTriggerProps) => {
  const tipId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const isHoveredRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<TipPosition | null>(null);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current || !tipRef.current) {
      setPosition(null);
      return;
    }

    const trigger = triggerRef.current.getBoundingClientRect();
    const tip = tipRef.current.getBoundingClientRect();
    const triggerCenter = trigger.left + trigger.width / 2;
    const maxLeft = document.documentElement.clientWidth - tip.width - SCREEN_MARGIN;
    const left = Math.max(SCREEN_MARGIN, Math.min(triggerCenter - tip.width / 2, maxLeft));
    const topAbove = trigger.top - tip.height - TRIGGER_GAP;
    const isBelow = topAbove < SCREEN_MARGIN;

    setPosition({
      top: isBelow ? trigger.bottom + TRIGGER_GAP : topAbove,
      left,
      arrowLeft: Math.max(ARROW_MARGIN, Math.min(triggerCenter - left, tip.width - ARROW_MARGIN)),
      isBelow
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const close = () => setIsOpen(false);
    const handlePointerDown = (event: PointerEvent) => {
      if (!triggerRef.current?.contains(event.target as Node)) {
        close();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [isOpen]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsOpen((prev) => isHoveredRef.current || !prev);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-disabled={isDisabled || undefined}
        aria-label={label}
        aria-describedby={isOpen ? tipId : undefined}
        className={className}
        onClick={handleClick}
        onMouseDown={(event) => event.preventDefault()}
        onPointerEnter={(event) => {
          if (event.pointerType === "mouse") {
            isHoveredRef.current = true;
            setIsOpen(true);
          }
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === "mouse") {
            isHoveredRef.current = false;
            setIsOpen(false);
          }
        }}
      >
        {children}
      </button>

      {isOpen && createPortal(
        <div
          ref={tipRef}
          id={tipId}
          role="tooltip"
          className="fixed z-[10000] w-max max-w-[240px] rounded bg-gray-800 px-3 py-2 text-center text-xs leading-5 text-white shadow-lg pointer-events-none"
          style={{
            top: position?.top ?? 0,
            left: position?.left ?? 0,
            visibility: position ? "visible" : "hidden"
          }}
        >
          {hint}
          <span
            className={`absolute h-2 w-2 -translate-x-1/2 rotate-45 bg-gray-800 ${position?.isBelow ? "-top-1" : "-bottom-1"}`}
            style={{ left: position?.arrowLeft ?? 0 }}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default HintTrigger;
